import * as Api from '@ViewModels';
import { InfiniteData } from '@tanstack/react-query';
import * as React from 'react';
import {
	invalidateCompanyTimeline,
	invalidateContactTimeline,
	useInfiniteCompanyTimeline,
	useInfiniteContactTimeline,
} from '../../../../../queries';

function isSameDay(d1: Date, d2: Date) {
	return d1.getDate() === d2.getDate() && d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
}

const useTimelineData = ({
	invalidateTimeline,
	timelinePageData,
}: {
	invalidateTimeline: () => void;
	timelinePageData: ReturnType<typeof useInfiniteCompanyTimeline>;
}) => {
	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, status } = timelinePageData;

	const dataWithDate: InfiniteData<Api.IPagedCollection<Api.TimelineEventViewModel<Api.ITimelineEvent> | Date>> =
		React.useMemo(() => {
			if (data) {
				let prevTime: Date = null;
				const newPages: Api.IPagedCollection<Api.TimelineEventViewModel<Api.ITimelineEvent> | Date>[] = [];
				for (const page of data.pages) {
					const values: (Date | Api.TimelineEventViewModel)[] = [];
					for (const value of page.values) {
						const sameDay = isSameDay(new Date(value.timestamp), new Date(prevTime));
						if (!sameDay || prevTime == null) {
							values.push(new Date(value.timestamp));
						}
						values.push(value);

						prevTime = new Date(value.timestamp);
					}
					newPages.push({
						...page,
						values,
					});
				}
				return {
					...data,
					pages: newPages,
				};
			}
			return data;
		}, [data]);
	return {
		data: dataWithDate,
		fetchNextPage,
		hasNextPage,
		invalidateTimeline,
		isFetchingNextPage,
		isLoading: isPending,
		status,
	} as const;
};

export const useContactTimelineData = ({
	contactId,
	eventTypes,
}: {
	contactId: string;
	eventTypes: Api.TimelineEventTypes[];
}) => {
	const timelinePageData = useInfiniteContactTimeline({
		contactId,
		eventTypes,
		meta: {
			errorLogging: {
				category: 'ContactTimelineData',
				action: 'ContactTimeline-Error',
			},
		},
	});
	return useTimelineData({
		invalidateTimeline: () => invalidateContactTimeline({ contactId }),
		timelinePageData,
	});
};

export const useCompanyTimelineData = ({
	companyId,
	eventTypes,
}: {
	companyId: string;
	eventTypes: Api.TimelineEventTypes[];
}) => {
	const timelinePageData = useInfiniteCompanyTimeline({
		companyId,
		eventTypes,
		meta: {
			errorLogging: {
				category: 'CompanyTimelineData',
				action: 'CompanyTimeline-Error',
			},
		},
	});
	return useTimelineData({
		invalidateTimeline: () => invalidateCompanyTimeline({ companyId }),
		timelinePageData,
	});
};
